import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardHeader, CardContent, CardTitle } from '../../../shadcn/components/ui/card';
import { Button } from '../../../shadcn/components/ui/button';
import { Badge } from '../../../shadcn/components/ui/badge';
import { useAppStore } from 'store/appStore';
import { FetchMeetingReportListResponse, MeetingReportListItem, CallType } from 'api-types';
import { ExportMeetingReportsButton } from './ExportMeetingReportsButton';
import { ArrowLeft, Search, X, ArrowDownLeft, ArrowUpRight } from 'lucide-react';
import { Pagination, PaginationItem } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { getBadgeClass, getDuration, getStartTime } from './CallReport';
import { getMeetingReportUIConfig } from './meetingReportConfig';
import { SpinnerLoader } from 'components/Loader/SpinnerLoader';
import { DatePicker } from 'components/DatePicker';
import { convertMonthToDateRange, getCurrentMonthString } from '../utils/utils';
import { ErrorCard } from '../../../components/ErrorHandling';

const PAGE_SIZE = 10;

interface CallLogsProps {
  reportUrlPath?: (meetingId: string, page: number, reportId: string) => string;
}

export function CallLogs({ reportUrlPath }: CallLogsProps = {}) {
  const navigate = useNavigate();
  const { meetingId, pageParam } = useParams<{ meetingId: string; pageParam?: string }>();
  const uiConfig = getMeetingReportUIConfig(meetingId);
  const fetchMeetingReportList = useAppStore(state => state.fetchMeetingReportList);
  const fetchWhatsappReportList = useAppStore(state => state.fetchWhatsappReportList);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(pageParam ? parseInt(pageParam, 10) : 1);
  const [meetingReportList, setMeetingReportList] = useState<FetchMeetingReportListResponse | null>(
    null
  );
  const [selectedMonth, setSelectedMonth] = useState<string>(() => {
    const stored = sessionStorage.getItem(`callLogs_selectedMonth_${meetingId}`);
    if (stored) {
      return stored;
    }
    return getCurrentMonthString();
  });
  const [reportType, setReportType] = useState<'voice' | 'whatsapp'>(() => {
    const stored = sessionStorage.getItem(`callLogs_reportType_${meetingId}`);
    return (stored as 'voice' | 'whatsapp') || uiConfig.defaultReportType;
  });
  const [searchQuery, setSearchQuery] = useState<string>(() => {
    const stored = sessionStorage.getItem(`callLogs_searchQuery_${meetingId}`);
    return stored || '';
  });
  const goBackToHome = () => {
    navigate('/');
  };

  const isInboundCall = (callType?: string) => {
    return callType === CallType.INBOUND || callType === 'inbound';
  };

  const isOutboundCall = (callType?: string) => {
    return callType === CallType.OUTBOUND || callType === 'outbound';
  };

  useEffect(() => {
    if (meetingId && !reportUrlPath) {
      navigate(`/meeting-report/${meetingId}/${page}`, { replace: true });
    }
    fetchData();
  }, [page, meetingId]);

  useEffect(() => {
    fetchData();
  }, [selectedMonth, reportType]);

  useEffect(() => {
    fetchData();
  }, [searchQuery]);

  const fetchData = async () => {
    if (!meetingId || !selectedMonth) return;

    const dateRange = convertMonthToDateRange(selectedMonth);

    setLoading(true);
    setError(null);
    try {
      let result;

      if (reportType === 'whatsapp') {
        result = await fetchWhatsappReportList({
          meetingId,
          page,
          pageSize: PAGE_SIZE,
          fromDate: dateRange.fromDate,
          toDate: dateRange.toDate,
          searchQuery: searchQuery || undefined,
        });
      } else {
        result = await fetchMeetingReportList({
          meetingId,
          page,
          pageSize: PAGE_SIZE,
          fromDate: dateRange.fromDate,
          toDate: dateRange.toDate,
          searchQuery: searchQuery || undefined,
        });
      }

      setMeetingReportList(result);
    } catch (err) {
      setError(`Error getting ${reportType} report. Please try again later.`);
      setMeetingReportList(null);
    } finally {
      setLoading(false);
    }
  };

  const total = meetingReportList?.total || 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const reports: MeetingReportListItem[] = meetingReportList?.reports || [];

  const handleMonthChange = (monthValue: string) => {
    setSelectedMonth(monthValue);
    setPage(1);
    if (meetingId) {
      sessionStorage.setItem(`callLogs_selectedMonth_${meetingId}`, monthValue);
    }
  };

  const handleReportTypeChange = (type: 'voice' | 'whatsapp') => {
    setReportType(type);
    setPage(1);
    if (meetingId) {
      sessionStorage.setItem(`callLogs_reportType_${meetingId}`, type);
    }
  };

  const handleSearchChange = (value: string) => {
    const trimmedValue = value.trim();
    setSearchQuery(trimmedValue);
    setPage(1);
    if (meetingId) {
      sessionStorage.setItem(`callLogs_searchQuery_${meetingId}`, trimmedValue);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setPage(1);
    if (meetingId) {
      sessionStorage.removeItem(`callLogs_searchQuery_${meetingId}`);
    }
  };

  return (
    <div>
      <div className="flex items-center mb-4 gap-2 justify-between">
        {!reportUrlPath && (
          <Button
            variant="ghost"
            className="p-2 h-10 flex items-center justify-center text-gray-700 hover:bg-gray-100"
            onClick={goBackToHome}
          >
            <ArrowLeft className="h-5 w-5" />
            <span className="text-base font-medium">Back</span>
          </Button>
        )}
        <div className="flex-1" />
        <div className="flex items-center gap-3">
          {uiConfig.showVoiceWhatsappToggle && (
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => handleReportTypeChange('voice')}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  reportType === 'voice'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Voice Calls
              </button>
              <button
                onClick={() => handleReportTypeChange('whatsapp')}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  reportType === 'whatsapp'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                WhatsApp
              </button>
            </div>
          )}
          <DatePicker selectedMonth={selectedMonth} onMonthChange={handleMonthChange} />
          {uiConfig.showExportCsv && <ExportMeetingReportsButton meetingId={meetingId} />}
        </div>
      </div>
      <Card className="shadow-sm border border-gray-200">
        <CardHeader className="bg-white border-b border-gray-200 p-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-2xl font-semibold">Call Logs</CardTitle>
            <div className="flex items-center gap-3">
              <div className="relative w-72">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, email, phone..."
                  value={searchQuery}
                  onChange={e => handleSearchChange(e.target.value)}
                  className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
                {searchQuery && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <SpinnerLoader />
            </div>
          ) : error ? (
            <div className="p-4">
              <ErrorCard
                title="Unable to load call logs"
                message={error}
                onRetry={fetchData}
                isRetrying={loading}
                showBackButton={false}
              />
            </div>
          ) : reports.length === 0 ? (
            <div className="flex justify-center items-center h-64">
              <p className="text-gray-500">No meeting found.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-100">
                  <tr className="text-left border-b border-gray-200">
                    <th className="px-6 py-2 text-sm font-semibold text-gray-700 w-[12%]">Name</th>
                    <th className="px-6 py-2 text-sm font-semibold text-gray-700 w-[15%]">Email</th>
                    <th className="px-6 py-2 text-sm font-semibold text-gray-700 w-[12%]">Phone</th>
                    <th className="px-6 py-2 text-sm font-semibold text-gray-700 w-[12%]">
                      WhatsApp
                    </th>
                    {uiConfig.showCallTypeColumn && (
                      <th className="px-6 py-2 text-sm font-semibold text-gray-700 w-[7%]">
                        Call Type
                      </th>
                    )}
                    {uiConfig.showVisaCategoryColumn && (
                      <th className="px-6 py-2 text-sm font-semibold text-gray-700 w-[12%]">
                        Visa Category
                      </th>
                    )}
                    <th className="px-6 py-2 text-sm font-semibold text-gray-700 w-[12%]">
                      Qualification
                    </th>
                    <th className="px-6 py-2 text-sm font-semibold text-gray-700 w-[10%]">
                      Start Time
                    </th>
                    <th className="px-6 py-2 text-sm font-semibold text-gray-700 w-[8%]">
                      Duration
                    </th>
                    <th className="px-6 py-2 text-sm font-semibold text-gray-700 w-[12%]">
                      Full Report
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {reports.map((report: MeetingReportListItem) => {
                    const name = `${report.firstName || ''} ${report.lastName || ''}`.trim();
                    return (
                      <tr key={report.id} className="transition-colors">
                        <td className="px-6 py-4">
                          <div className="truncate">{name || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 text-sm whitespace-nowrap">
                          {!report.email || report.email === 'null' ? (
                            'N/A'
                          ) : (
                            <div className="truncate" title={report.email}>
                              {report.email}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm whitespace-nowrap">
                          {report.phone || 'N/A'}
                        </td>
                        <td className="px-6 py-4 text-sm whitespace-nowrap">
                          {report.whatsapp || 'N/A'}
                        </td>
                        {uiConfig.showCallTypeColumn && (
                          <td className="px-6 py-4 text-sm whitespace-nowrap">
                            {isInboundCall(report.callType) ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                <ArrowDownLeft className="h-3 w-3" />
                                Inbound
                              </span>
                            ) : isOutboundCall(report.callType) ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                                <ArrowUpRight className="h-3 w-3" />
                                Outbound
                              </span>
                            ) : (
                              'N/A'
                            )}
                          </td>
                        )}
                        {uiConfig.showVisaCategoryColumn && (
                          <td className="px-6 py-4 text-sm whitespace-nowrap">
                            {report.visaCategory || 'N/A'}
                          </td>
                        )}
                        <td className="px-6 py-4 text-sm whitespace-nowrap">
                          {report.qualificationScore ? (
                            <Badge
                              variant="outline"
                              className={`px-3 py-1 font-semibold text-sm rounded-full border-2 ${getBadgeClass(
                                report.qualificationScore
                              )}`}
                            >
                              {report.qualificationScore}
                            </Badge>
                          ) : (
                            'N/A'
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm whitespace-nowrap">
                          {report.startedAt ? getStartTime(report.startedAt) : 'N/A'}
                        </td>
                        <td className="px-6 py-4 text-sm whitespace-nowrap">
                          {report.endedAt && report.startedAt
                            ? getDuration(report.startedAt, report.endedAt)
                            : 'N/A'}
                        </td>
                        <td className="px-2 py-4">
                          <Button
                            variant="ghost"
                            className="font-semibold text-gray-700 hover:bg-gray-100"
                            size="sm"
                            onClick={() => {
                              const url =
                                reportUrlPath && meetingId
                                  ? reportUrlPath(meetingId, page, report.id || '')
                                  : reportType === 'whatsapp'
                                    ? `/meeting-fullreport/${meetingId}/${page}/${report.id}?type=whatsapp`
                                    : `/meeting-fullreport/${meetingId}/${page}/${report.id}`;
                              window.open(url, '_blank');
                            }}
                            disabled={!report.id}
                          >
                            View Full Report
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {totalPages > 1 && (
                <div className="flex flex-col justify-center items-center py-4 gap-2">
                  <Pagination
                    variant="outlined"
                    shape="rounded"
                    count={totalPages}
                    page={page}
                    showFirstButton={false}
                    showLastButton={false}
                    onChange={(e, val) => setPage(val)}
                    renderItem={item => (
                      <PaginationItem
                        slots={{ previous: ArrowBackIcon, next: ArrowForwardIcon }}
                        {...item}
                        sx={{
                          '&.MuiPaginationItem-root': {
                            borderRadius: '4px',
                            margin: '0 2px',
                          },
                        }}
                      />
                    )}
                    sx={{
                      '& .MuiPaginationItem-root': {
                        borderColor: 'rgba(0, 0, 0, 0.12)',
                        fontWeight: 'medium',
                      },
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
